import { useLocalStorageValue, useWindowSize } from "@react-hookz/web";
import { isEmpty } from "ramda";
import { useCallback, useMemo } from "react";
import { DataTableRow, DataTableRowProps, DataTableTable, Pagination } from "@/components/molecules";
import { DataTableV2 } from "@/components/molecules/DataTableV2/DataTable";
import { useOperator, usePagination, useProfile, useSorting } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { bookingsPageSelector } from "@/redux/slices/booking/selectors";
import { Booking } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";
import { allColumns } from "./fixtures";
import { generateBookingColumns, generateBookingData } from "./helpers";
import { BookingsTableData, TableColumn } from "./types";

const defaultColumns = allColumns.filter((i) => i.isDefault);
const fixedColumns = allColumns.filter((i) => i.isFixed);

export interface BookingsTableV2Props extends React.ComponentPropsWithoutRef<"div"> {
  items: Booking[];
  compactView: boolean;
  setSelectedBookingId?: (id: string) => void;
  selectedBookingId?: string;
}

export const BookingsTableV2 = ({
  items,
  compactView = false,
  setSelectedBookingId,
  selectedBookingId,
  ...props
}: BookingsTableV2Props) => {
  const { providerCode, id: operatorId } = useOperator();
  const profile = useProfile();
  const { width } = useWindowSize();
  const { sorting, setSorting } = useSorting();

  const { value: savedColumns = [] } = useLocalStorageValue(`${operatorId}:booking-columns:v2`, { defaultValue: [] });
  const data = useMemo(() => generateBookingData(items, profile, providerCode), [items, profile, providerCode]);

  const tableColumns = useMemo(() => {
    const mappedColumns = (Array.isArray(savedColumns) ? savedColumns : [])
      .map(id => allColumns.find(c => c.id === id))
      .filter((c): c is TableColumn => Boolean(c));

    if (isEmpty(savedColumns) || isEmpty(mappedColumns)) {
      return width < 640
        ? generateBookingColumns(defaultColumns.filter(i => i.isVisibleMobile))
        : generateBookingColumns(defaultColumns);
    }

    // Keep ALL fixed columns at the left in their declared order
    const fixedIds = new Set(fixedColumns.map(c => c.id));
    const nonFixedMapped = mappedColumns.filter(c => !fixedIds.has(c.id));

    const columns: TableColumn[] = [
      ...fixedColumns,       // offloadIcon, id (both pinned left)
      ...nonFixedMapped,     // user-picked, ordered
    ];

    return width < 640
      ? generateBookingColumns(columns.filter(i => i.isVisibleMobile))
      : generateBookingColumns(columns);
  }, [savedColumns, width]);

  const getRowProps = useCallback(
    (row: DataTableRow<BookingsTableData>, _table: DataTableTable<BookingsTableData>) => {
      const rate = row.original.metadata.transferRate;
      const isSpecial = rate === "daily_rate" || rate === "hourly_rate";
      const isSelected = row.original.bookingId === selectedBookingId;

      const rateClass =
        rate === "daily_rate"
          ? "!bg-[#EDF6FF] hover:!bg-[#DAECFF]"
          : rate === "hourly_rate"
          ? "!bg-[#FBF4E2] hover:!bg-[#FFF3D3]"
          : "";

      const handleClick = () => setSelectedBookingId?.(row.original.bookingId);

      const rowProps: Partial<DataTableRowProps<BookingsTableData>> = {
        id: `booking-${row.original.bookingId}`,
        className: clsx(
          "cursor-pointer booking-row",
          rateClass,
          !isSpecial && "hover:bg-neutral-gray",
          isSelected && "!bg-primary-light",
          { "[&>td]:text-danger": !row.original.metadata.isVerified }
        ),
        onClick: handleClick,
        role: "button",
        tabIndex: 0,
        "aria-selected": isSelected,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        },
      };

      return rowProps;
    },
    [setSelectedBookingId, selectedBookingId]
  );

  const tableClass = "max-sm:[&_.th]:hidden";
  const rowClass =
    "[&_.tr]:border-bottom-1 [&_.tr]:px-0 [&_.tr]:grid sm:[&_.tr]:flex [&_.tr]:gap-0 [&_.tr]:grid-cols-4";
  const cellClass =
    "max-sm:[&_.td]:!w-auto [&_.td]:flex-col sm:[&_.td]:flex-row max-sm:[&_.td]:items-start [&_.td]:p-2";
  const compactViewClass = "group compact-view [&_.tr]:!p-0 [&_.td]:!p-1 [&_.thead_.th]:!p-1";

  return (
    <DataTableV2
      className={clsx(tableClass, rowClass, cellClass, compactView && compactViewClass)}
      columns={tableColumns}
      data={data}
      sorting={sorting}
      setSorting={setSorting}
      getRowProps={getRowProps}
      {...props}
    />
  );
};

/* Pagination unchanged */
const BookingsTablePagination = () => {
  const { current, count, total, size } = useAppSelector(bookingsPageSelector);
  const { handlePageClick, handleSizeChange } = usePagination("bookings");

  return (
    <Pagination
      className="my-4 max-lg:mb-16"
      onPageClick={handlePageClick}
      onSizeChange={handleSizeChange}
      currentPage={current}
      pageSize={size}
      pageSizes={[50, 100, 200]}
      totalItems={total}
      pageCount={count}
    />
  );
};

BookingsTableV2.Pagination = BookingsTablePagination;